import { useEffect, useRef } from 'react'

import type { SfxName } from '../audio/sfx'
import { playSfx } from '../audio/sfx'

/**
 * A decorative character standing next to the board — devil, cat, ghost.
 *
 * Mounted as `<object>` rather than `<img>` for a reason that predates the
 * sound: SMIL animation runs either way, but only an object's inner
 * document is reachable, which is what lets the mascot's win-wink be fired
 * from React and what lets every character react to its own click
 * (`begin="ghostBody.click"` and friends inside the SVG).
 *
 * The sound is wired to the SAME element ids the SVG already animates on.
 * Listening on the whole document instead would fire on the transparent
 * padding around the character, so the scenery would speak when the player
 * clicked nothing at all.
 */
export function StageCharacter({
  className,
  src,
  sfx,
  hotspots,
  objectRef,
}: {
  className: string
  src: string
  /** Sound for a click on this character. */
  sfx: SfxName
  /** Ids inside the SVG that count as the character itself. */
  hotspots: readonly string[]
  /** Escape hatch for the mascot, whose wink is triggered from App. */
  objectRef?: React.RefObject<HTMLObjectElement | null>
}): React.JSX.Element {
  const own = useRef<HTMLObjectElement | null>(null)

  useEffect(() => {
    const host = objectRef?.current ?? own.current
    if (!host) return

    let cleanup: (() => void) | null = null

    /**
     * An `<object>` reports a contentDocument long before it holds the SVG:
     * until the fetch lands it is a blank document, where every hotspot id
     * is missing. So "is it loaded" is answered by looking for the hotspots
     * themselves — the `load` event alone is not enough, because a cached
     * file can fire it before this effect ever runs.
     */
    const attach = (): boolean => {
      const doc = host.contentDocument
      if (!doc) return false
      const targets = hotspots
        .map((id) => doc.getElementById(id))
        .filter((el): el is HTMLElement => el != null)
      if (targets.length === 0) return false

      const play = (): void => playSfx(sfx, 0.7)
      for (const el of targets) el.addEventListener('click', play)
      cleanup = () => {
        for (const el of targets) el.removeEventListener('click', play)
      }
      return true
    }

    // Poll until the document carries the art, then stop. Bounded so a
    // theme whose SVG lacks these ids costs a few frames, not a timer that
    // runs for the whole session.
    let tries = 0
    const timer = window.setInterval(() => {
      if (attach() || ++tries > 40) window.clearInterval(timer)
    }, 100)
    if (attach()) window.clearInterval(timer)

    return () => {
      window.clearInterval(timer)
      cleanup?.()
    }
  }, [sfx, hotspots, src, objectRef])

  return (
    <object
      ref={objectRef ?? own}
      className={className}
      data={src}
      type="image/svg+xml"
      tabIndex={-1}
      aria-hidden="true"
    />
  )
}
